/**
 * National (10-digit) digits of a US phone value. An 11-digit number with a
 * leading country code "1" ("+1 (555) 123-4567", autofill, paste) drops the 1 —
 * area codes never start with 1 — so a valid number is never read as a
 * different, wrong one (formatPhone used to keep the 1 and cut the last digit)
 * or as incomplete (KAN-774 "Faltan 1: Teléfono" on a valid phone).
 */
export function phoneNationalDigits(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
}

export function formatPhone(value) {
  const numbers = phoneNationalDigits(value);
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `(${numbers.slice(0,3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0,3)}) ${numbers.slice(3,6)}-${numbers.slice(6,10)}`;
}

/** Digit count of a phone value — completeness checks ("10 digits or empty"). */
export function phoneDigitCount(value) {
  return phoneNationalDigits(value).length;
}

/** Trim + collapse internal whitespace runs — names, addresses, free text. */
export function normalizeSpaces(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

export function formatCurrency(value) {
  const n = Number(value || 0)
  return n.toLocaleString('en-US', {
    style: "currency",
    currency: "USD"
  })
}

/** Compact hero-stat currency — "$70.2k" reads faster than "$70,243.96" at dashboard scale. */
export function formatCompactCurrency(amount) {
  if (!Number.isFinite(amount)) return '$0';
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs < 1000) return `${sign}$${Math.round(abs).toLocaleString()}`;
  if (abs < 1000000) {
    const k = abs / 1000;
    const formatted = k >= 100 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '');
    return `${sign}$${formatted}k`;
  }
  const m = abs / 1000000;
  const formatted = m >= 100 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, '');
  return `${sign}$${formatted}M`;
}

export function toNumber(v) {
  return Number(v || 0)
}

/** For number/money inputs: show empty string instead of 0 so typing doesn't produce "0..." */
export function numberInputDisplay(v) {
  if (v === '' || v === undefined || v === null) return ''
  if (v === 0 || v === '0') return ''
  return String(v)
}

/** Format a raw number string with commas for display in inputs (e.g. "10000" → "10,000"). */
export function fmtMoneyInput(raw, locale = 'en-US') {
  if (raw === '' || raw === undefined || raw === null) return '';
  const str = String(raw).replace(/,/g, '');
  const num = parseFloat(str);
  if (!Number.isFinite(num)) return str;
  const [int, dec] = str.split('.');
  const intFormatted = Number(int).toLocaleString(locale);
  return dec !== undefined ? `${intFormatted}.${dec}` : intFormatted;
}

/** Strip commas from a formatted money string to get the raw value. */
export function stripCommas(str) {
  return String(str || '').replace(/,/g, '');
}

/** Caps a money input string to `maxDecimals` decimal places and optional `maxValue`. */
export function clampMoneyInput(raw, maxValue = Infinity, maxDecimals = 3) {
  if (raw === '' || raw === undefined || raw === null) return ''
  const str = String(raw)
  const dotIdx = str.indexOf('.')
  if (dotIdx !== -1 && str.length - dotIdx - 1 > maxDecimals) {
    return str.slice(0, dotIdx + maxDecimals + 1)
  }
  const parsed = parseFloat(str)
  if (Number.isFinite(parsed) && parsed > maxValue) {
    return String(maxValue)
  }
  return str
}

/**
 * Clamp a percent input string.
 * Strips non-numeric chars (allows one decimal point), caps decimals, clamps to [min, max].
 * Returns { value: string, didClamp: boolean }.
 * Empty input returns { value: '', didClamp: false }.
 */
export function clampPercentInput(raw, { min = 0.01, max = 100, decimals = 2 } = {}) {
  if (raw === '' || raw === undefined || raw === null) return { value: '', didClamp: false }
  const str = String(raw)
  // Sanitize: keep digits + at most one decimal point
  let sanitized = ''
  let sawDot = false
  for (const ch of str) {
    if (ch >= '0' && ch <= '9') { sanitized += ch }
    else if (ch === '.' && !sawDot) { sanitized += ch; sawDot = true }
  }
  if (sanitized === '' || sanitized === '.') {
    return { value: '0', didClamp: false }
  }
  // Cap decimal places
  const dotIdx = sanitized.indexOf('.')
  if (dotIdx !== -1 && sanitized.length - dotIdx - 1 > decimals) {
    sanitized = sanitized.slice(0, dotIdx + decimals + 1)
  }
  const parsed = parseFloat(sanitized)
  if (!Number.isFinite(parsed)) return { value: '0', didClamp: false }
  if (parsed > max) {
    return { value: max.toFixed(decimals), didClamp: true }
  }
  return { value: sanitized, didClamp: false }
}